import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { getStudentCouponTypes, getStudentCoupons, createCoupon, deleteCoupon } from '../api/coupons2.api';
import { IStudentCouponType, IGetStudentCouponTypes, IStudentCoupon, IGetStudentCoupons, IStudentCouponReq } from '../models/coupons2.model';

interface IStudentCouponState {
  studentCouponTypes?: IStudentCouponType[];
  studentCoupons?: IStudentCoupon[];
  isLoading: boolean;
  error?: AxiosError | string;
};

const initialState: IStudentCouponState = {
  studentCouponTypes: [],
  studentCoupons: [],
  isLoading: false
};

const studentCouponSlicer = createSlice({
  name: 'studentCoupon',
  initialState,
  reducers: {
    getStudentCouponTypesStart: (state: IStudentCouponState) => {
      state.studentCouponTypes = undefined;
      state.isLoading = true;
      state.error = undefined;
    },
    getStudentCouponTypesSuccess: (state: IStudentCouponState, action: PayloadAction<IGetStudentCouponTypes>) => {
      state.studentCouponTypes = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getStudentCouponTypesFailure: (state: IStudentCouponState, action: PayloadAction<AxiosError|string>) => {
      state.studentCouponTypes = undefined;
      state.isLoading = false;
      state.error = action.payload;
    },
    getStudentCouponsStart: (state: IStudentCouponState) => {
      state.studentCoupons = [];
      state.isLoading = true;
      state.error = undefined;
    },
    getStudentCouponsSuccess: (state: IStudentCouponState, action: PayloadAction<IGetStudentCoupons>) => {
      state.studentCoupons = action.payload.data;
      state.isLoading = false;
      state.error = undefined;
    },
    getStudentCouponsFailure: (state: IStudentCouponState, action: PayloadAction<AxiosError|string>) => {
      state.studentCoupons = [];
      state.isLoading = false;
      state.error = action.payload;
    },
    requestStart: (state: IStudentCouponState) => {
      state.isLoading = true;
      state.error = undefined;
    },
    requestFailure: (state: IStudentCouponState, action: PayloadAction<AxiosError|string>) => {
      state.isLoading = false;
      state.error = action.payload;
    },
    createSuccess: (state: IStudentCouponState) => {
      state.isLoading = false;
      state.error = undefined;
    },
    deleteSuccess: (state: IStudentCouponState) => {
      state.isLoading = false;
      state.error = undefined;
    },
  }
});

export const {
  getStudentCouponTypesStart,
  getStudentCouponTypesSuccess,
  getStudentCouponTypesFailure,
  getStudentCouponsStart,
  getStudentCouponsSuccess,
  getStudentCouponsFailure,
  requestStart,
  requestFailure,
  createSuccess,
  deleteSuccess
} = studentCouponSlicer.actions;

export default studentCouponSlicer.reducer;

export const doGetStudentCouponTypes = (campusCode: string, memberCode: string): any => async (dispatch: any) => {
  try {
    dispatch(getStudentCouponTypesStart());
    const response = await getStudentCouponTypes(campusCode, memberCode);
    dispatch(getStudentCouponTypesSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getStudentCouponTypesFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doGetStudentCoupons = (memberCode: string): any => async (dispatch: any) => {
  try {
    dispatch(getStudentCouponsStart());
    const response = await getStudentCoupons(memberCode);
    dispatch(getStudentCouponsSuccess(response.data));
    return response;
  } catch (e: any) {
    dispatch(getStudentCouponsFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doCreateCoupon = (couponCode: number, memberCode: string, data: IStudentCouponReq): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await createCoupon(couponCode, memberCode, data);
    dispatch(createSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};

export const doDeleteCoupon = (couponCode: number): any => async (dispatch: any) => {
  try {
    dispatch(requestStart());
    const response = await deleteCoupon(couponCode);
    dispatch(deleteSuccess());
    return response;
  } catch (e: any) {
    dispatch(requestFailure(e.response?.data?.data || e.message));
    return e.response;
  }
};
